import { objectKeys, zCustom } from '@nucleus/lib-shape';
import { zKey } from '@nucleus/ncu-key-prefix';
import { ISO8601Schema } from '@nucleus/src-platform/data';
import { imageWebSchema } from '@nucleus/types/media/image';
import {
  SharingMetadataApi,
  SharingMetadataEntityItemApi,
  SharingMetadataItem,
  SharingMetadataItemApi,
} from '@nucleus/types/web/sharing';
import * as yup from 'yup';
import { z } from 'zod';
import { PublishableEngineSchemaDeprecated } from './PublishableEngine';
import { PublishableEntitySchemaDeprecated } from './PublishableEntity';
import { PublishablePrivacyControlsSchema, PublishablePrivacyControlsSchemaDeprecated } from './PublishablePrivacy';

export type PublishableRevisionType = z.infer<typeof PublishableRevisionTypeSchema>;

export type PublishableRevision = z.infer<typeof PublishableRevisionSchema>;

/**
 * @deprecated this should be replaced with PublishableRevision
 */
export type PublishableRevisionApi = Omit<PublishableRevision, 'sharingMetadata'> & {
  sharingMetadata?: SharingMetadataApi;
};

export type PublishableRevisionWeb = z.infer<typeof PublishableRevisionWebSchema>;

const PublishableRevisionTypeSchema = z.enum(['autoSave', 'userSave', 'publish', 'unpublish']);

// TODO: import ImageSchema when it is available
const imageSchemaTemporary = z.object({
  fileId: z.string(),
});

const coerceEmptyStringToUndefined = <T extends z.ZodString | z.ZodOptional<z.ZodString>>(schema: T) =>
  z.preprocess<T, T>((value) => (value === '' ? undefined : value) as T, schema) as z.ZodPipe<
    z.ZodTransform<T, string | undefined>,
    T
  >;

type OptionalImageSchema = z.ZodOptional<typeof imageSchemaTemporary>;

export const PublishableSharingMetadataSchema = z.object({
  enabled: z.boolean().optional(),
  title: coerceEmptyStringToUndefined(z.string().optional()),
  description: coerceEmptyStringToUndefined(z.string().optional()),
  image: z.preprocess(
    (value) => (typeof value === 'object' && objectKeys(value ?? {}).length > 0 ? value : undefined),
    imageSchemaTemporary.optional()
  ) as z.ZodPipe<z.ZodTransform<OptionalImageSchema, z.infer<typeof imageSchemaTemporary>>, OptionalImageSchema>,
});

export const PublishableEntitySharingMetadataSchema = PublishableSharingMetadataSchema.extend({
  keywords: z.array(z.string()).optional(),
  noindex: z.boolean().optional(),
});

export const PublishableSharingMetadataWebSchema = z.object({
  enabled: z.boolean().optional(),
  title: coerceEmptyStringToUndefined(z.string().optional()),
  description: coerceEmptyStringToUndefined(z.string().optional()),
  image: imageWebSchema.optional(),
});

export const PublishableEntitySharingMetadataWebSchema = PublishableSharingMetadataWebSchema.extend({
  keywords: z.array(z.string()).optional(),
  noindex: z.boolean().optional(),
});

export const PublishableRevisionSchema = z.object({
  id: zKey.prefixedKey(),
  index: z.number().min(1).default(1),
  engineId: zKey.prefixedKey(),
  entityId: zKey.prefixedKey(),
  revisionType: PublishableRevisionTypeSchema,
  revisionSource: z.unknown().optional(),
  title: z.string().optional(),
  abstract: z.string().optional(),
  description: coerceEmptyStringToUndefined(z.string().optional()),
  privacy: PublishablePrivacyControlsSchema.default({
    active: false,
    criteria: {},
  }),
  publishedAt: zCustom.iso().optional(),
  sharingMetadata: z
    .object({
      entity: PublishableEntitySharingMetadataSchema.optional(),
      facebook: PublishableSharingMetadataSchema.optional(),
      twitter: PublishableSharingMetadataSchema.optional(),
    })
    .optional(),
  excludeFromListing: z.boolean().optional(),
  revertedAt: zCustom.iso().optional(),
  revertedBy: z.string().optional(),
  unpublishedAt: zCustom.iso().optional(),
  unpublishedBy: z.string().optional(),
  createdAt: zCustom.iso(),
  createdBy: z.string().trim(),
});

export const PublishableRevisionWebSchema = PublishableRevisionSchema.extend({
  sharingMetadata: z.object({
    entity: PublishableEntitySharingMetadataWebSchema.optional(),
    facebook: PublishableSharingMetadataWebSchema.optional(),
    twitter: PublishableSharingMetadataWebSchema.optional(),
  }),
});

/**
 * @deprecated this schema is deprecated and will be removed in the future
 */
const PublishableRevisionTypeSchemaDeprecated: yup.SchemaOf<PublishableRevisionType> = yup
  .mixed()
  .oneOf(['autoSave', 'userSave', 'publish', 'unpublish']);

/**
 * @deprecated this schema is deprecated and will be removed in the future
 */
const transformEmptyStringSchemaDeprecated = yup.string().transform((value) => (value !== '' ? value : undefined));

/**
 * @deprecated this schema is deprecated and will be removed in the future
 */
export const PublishableSharingMetadataSchemaDeprecated: yup.SchemaOf<SharingMetadataItem> = yup.object({
  enabled: yup.boolean().optional(),
  fileBondId: transformEmptyStringSchemaDeprecated.optional(),
  title: transformEmptyStringSchemaDeprecated.optional(),
  description: transformEmptyStringSchemaDeprecated.optional(),
  image: yup.object({ fileId: yup.string() }).optional(),
});

/**
 * @deprecated this schema is deprecated and will be removed in the future
 * this schema will not be replaced with a zod schema use `PublishableSharingMetadataSchema` instead
 */
export const PublishableSharingMetadataApiSchemaDeprecated: yup.SchemaOf<SharingMetadataItemApi> =
  PublishableSharingMetadataSchemaDeprecated;

/**
 * @deprecated this schema is deprecated and will be removed in the future
 */
export const PublishableEntitySharingMetadataSchemaDeprecated: yup.SchemaOf<SharingMetadataEntityItemApi> =
  PublishableSharingMetadataSchemaDeprecated.shape({
    keywords: yup.array().of(yup.string()).optional(),
    noindex: yup.boolean().optional(),
  });

/**
 * @deprecated this schema is deprecated and will be removed in the future
 * this schema will not be replaced with a zod schema use `PublishableEntitySharingMetadataSchema` instead
 */
export const PublishableEntitySharingMetadataApiSchemaDeprecated: yup.SchemaOf<SharingMetadataEntityItemApi> =
  PublishableEntitySharingMetadataSchemaDeprecated;

/**
 * @deprecated this schema is deprecated and will be removed in the future
 */
export const PublishableRevisionSchemaDeprecated: yup.SchemaOf<PublishableRevision> = yup.object({
  id: yup.string().required(),
  index: yup.number().min(1).required().default(1),
  engineId: PublishableEngineSchemaDeprecated.fields.id.required(),
  entityId: PublishableEntitySchemaDeprecated.fields.id.required(),
  revisionType: PublishableRevisionTypeSchemaDeprecated.required(),
  revisionSource: yup.mixed().optional(),
  title: yup.string().optional(),
  abstract: yup.string(),
  description: transformEmptyStringSchemaDeprecated.optional(),
  privacy: PublishablePrivacyControlsSchemaDeprecated.required(),
  publishedAt: ISO8601Schema.optional(),
  sharingMetadata: yup
    .object({
      entity: PublishableEntitySharingMetadataSchemaDeprecated.default(undefined),
      facebook: PublishableSharingMetadataSchemaDeprecated.default(undefined),
      twitter: PublishableSharingMetadataSchemaDeprecated.default(undefined),
    })
    .default(undefined),
  excludeFromListing: yup.boolean(),
  revertedAt: ISO8601Schema,
  revertedBy: yup.string(),
  unpublishedAt: ISO8601Schema,
  unpublishedBy: yup.string(),
  createdAt: ISO8601Schema.required(),
  createdBy: yup.string().required(),
});

/**
 * @deprecated this schema is deprecated and will be removed in the future
 * this schema will not be replaced with a zod schema use `PublishableRevisionSchema` instead
 */
export const PublishableRevisionApiSchemaDeprecated: yup.SchemaOf<PublishableRevisionApi> =
  PublishableRevisionSchemaDeprecated.concat(
    yup.object({
      sharingMetadata: yup
        .object({
          entity: PublishableEntitySharingMetadataApiSchemaDeprecated.default(undefined),
          facebook: PublishableSharingMetadataApiSchemaDeprecated.default(undefined),
          twitter: PublishableSharingMetadataApiSchemaDeprecated.default(undefined),
        })
        .default(undefined),
    })
  );
